import { createRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { Route as appRoute } from '../../app.js';
import { useAuthStore } from '../../../lib/auth.js';
import { canSeeOversightQuotes } from '../../../lib/capabilities.js';
import { useOversightQuote } from '../../../hooks/useOversight.js';
import type { QuoteLineView, QuoteView } from '../../../hooks/useQuotes.js';
import { activeScholarship, scholarshipLabel } from '../../../lib/pricing.js';
import { centsToAudDisplay } from '../../../lib/money.js';
import { formatDate } from '../../../lib/format.js';
import { editorLabel } from '../../../lib/editor-label.js';
import { formatQuoteDate, visaSubclassDisplay, QUOTE_IMMUTABLE_NOTE } from '../../../lib/quotes.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/oversight/quotes/$quoteId',
  // Same gate as the overview: view_all_branches, admitting a tenant owner/accounts
  // reader (tenant-scoped) OR a super_admin (cross-agency, Brief QUOTES-OVERSIGHT).
  // READ-ONLY — the server enforces it; there is no send/edit/delete endpoint here.
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    const served = !!user?.tenantId || user?.role === 'super_admin';
    if (!canSeeOversightQuotes(user) || !served) throw redirect({ to: '/app' });
  },
  component: OversightQuoteDetailRoute,
});

/** The navigation seam — unexported, never rendered by a test (the quote-detail precedent). */
function OversightQuoteDetailRoute() {
  const { quoteId } = useParams({ from: '/app/oversight/quotes/$quoteId' });
  const navigate = useNavigate();
  return (
    <OversightQuoteDetailPage
      quoteId={quoteId}
      onBack={() => navigate({ to: '/app/oversight/quotes' })}
    />
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}

function LineRow({ line: l }: { line: QuoteLineView }) {
  const sch = activeScholarship({
    kind: l.scholarshipKind,
    percentBp: l.scholarshipPercentBp,
    amountCents: l.scholarshipAmountCents,
  });
  return (
    <tr className="align-top">
      <td className="px-5 py-3 text-sm text-muted">{l.lineOrder}</td>
      <td className="px-5 py-3 min-w-[16rem]">
        <span className="block text-sm text-ink">{l.courseName}</span>
        <span className="block text-xs text-muted">{l.providerName}</span>
        {l.cricosCode && <span className="block text-xs text-muted">CRICOS {l.cricosCode}</span>}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-navy">
        {centsToAudDisplay(l.feeCents)}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-ink">
        {sch ? scholarshipLabel(sch) : '—'}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
        {centsToAudDisplay(l.netFeeCents)}
      </td>
    </tr>
  );
}

/**
 * One issued quote as an oversight reader sees it — the locked, permanent record,
 * READ-ONLY (Brief QUOTES-OVERSIGHT). No Send, no edit, no delete: this surface
 * has no such endpoint, and opening it already left a server-side audit trace.
 * Every figure is the server's frozen value; nothing here is recomputed.
 */
export function OversightQuoteDetailPage({
  quoteId,
  onBack,
}: {
  quoteId: string;
  onBack: () => void;
}) {
  const { data, isLoading, error, refetch } = useOversightQuote(quoteId);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const quote: QuoteView | undefined = data?.quote;

  return (
    <div className="max-w-4xl">
      <button className="mb-4 text-sm text-muted underline" onClick={onBack}>
        Back to quotes overview
      </button>

      {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading quote…</p>}
      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
          Couldn&apos;t load the quote: {(error as Error).message}{' '}
          <button className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}
      {!isLoading && !error && !quote && (
        <p className="px-1 py-6 text-sm text-muted">Quote not found.</p>
      )}

      {!isLoading && !error && quote && (
        <>
          <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">
            Quote
          </div>
          <h1 className="font-serif text-4xl tracking-tighter2 mb-2">{quote.quoteNumber}</h1>
          <p className="text-muted text-sm mb-6">{QUOTE_IMMUTABLE_NOTE}</p>

          <dl className="surface p-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Quote date" value={formatQuoteDate(quote.quoteDate)} />
            <Field label="Issued by" value={editorLabel(quote, currentUserId)} />
            <Field label="Created" value={formatDate(quote.createdAt)} />
          </dl>

          <h2 className="font-serif text-xl text-ink mt-8 mb-2">Client</h2>
          <dl className="surface p-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Name" value={quote.client.name} />
            <Field label="Email" value={quote.client.email} />
            <Field label="Phone" value={quote.client.phone ?? '—'} />
            <Field label="Visa" value={visaSubclassDisplay(quote.client.visaSubclass)} />
            <Field label="Nationality" value={quote.client.nationality ?? '—'} />
          </dl>

          <h2 className="font-serif text-xl text-ink mt-8 mb-2">Courses</h2>
          <div className="surface p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">#</th>
                  <th className="px-5 py-3 font-medium">Course</th>
                  <th className="px-5 py-3 font-medium text-right">Fee</th>
                  <th className="px-5 py-3 font-medium text-right">Scholarship</th>
                  <th className="px-5 py-3 font-medium text-right">Net fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {quote.lines.map((l) => (
                  <LineRow key={l.id} line={l} />
                ))}
              </tbody>
              <tfoot className="bg-cream/60 border-t border-line">
                <tr>
                  <td className="px-5 py-3 text-sm text-ink" colSpan={4}>
                    Total (net)
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
                    {centsToAudDisplay(quote.totalNetFeeCents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {quote.notes && (
            <>
              <h2 className="font-serif text-xl text-ink mt-8 mb-2">Notes</h2>
              <p className="surface p-5 whitespace-pre-wrap text-sm text-ink-soft">{quote.notes}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
